<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, required: true },
  value: { type: Object, default: () => ({}) },
  allowInherit: { type: Boolean, default: false },
});

const emit = defineEmits(['save']);
const { t } = useI18n();

const state = reactive({
  enabledState: 'disabled',
  autonomyMode: 'supervised',
  confidenceThresholdInherited: false,
  confidenceThreshold: 0.85,
  redactionInherited: false,
  redactionEnabled: true,
  logRetentionInherited: false,
  logRetentionDays: 30,
});

const enabledOptions = computed(() => {
  const options = [];
  if (props.allowInherit) {
    options.push({ value: 'inherit', label: t('CAPTAIN.AI_NATIVE.INHERIT') });
  }
  options.push(
    { value: 'enabled', label: t('CAPTAIN.AI_NATIVE.ENABLED') },
    { value: 'disabled', label: t('CAPTAIN.AI_NATIVE.DISABLED') }
  );
  return options;
});

const modeOptions = computed(() => {
  const options = [];
  if (props.allowInherit) {
    options.push({ value: 'inherit', label: t('CAPTAIN.AI_NATIVE.INHERIT') });
  }
  options.push(
    { value: 'off', label: t('CAPTAIN.AI_NATIVE.MODES.OFF') },
    { value: 'supervised', label: t('CAPTAIN.AI_NATIVE.MODES.SUPERVISED') },
    { value: 'automatic', label: t('CAPTAIN.AI_NATIVE.MODES.AUTOMATIC') }
  );
  return options;
});

const updateState = value => {
  const stored = value?.stored || value || {};
  if (
    props.allowInherit &&
    (stored.enabled === undefined || stored.enabled === null)
  ) {
    state.enabledState = 'inherit';
  } else {
    state.enabledState = stored.enabled ? 'enabled' : 'disabled';
  }

  state.autonomyMode =
    stored.autonomy_mode || (props.allowInherit ? 'inherit' : 'supervised');
  state.confidenceThresholdInherited =
    props.allowInherit &&
    (stored.automatic_confidence_threshold === undefined ||
      stored.automatic_confidence_threshold === null);
  state.confidenceThreshold = Number(
    stored.automatic_confidence_threshold ??
      value?.automatic_confidence_threshold ??
      0.85
  );
  state.redactionInherited =
    props.allowInherit &&
    (stored.redaction_enabled === undefined ||
      stored.redaction_enabled === null);
  state.redactionEnabled =
    stored.redaction_enabled ?? value?.redaction_enabled ?? true;
  state.logRetentionInherited =
    props.allowInherit &&
    (stored.log_retention_days === undefined ||
      stored.log_retention_days === null);
  state.logRetentionDays = Number(
    stored.log_retention_days ?? value?.log_retention_days ?? 30
  );
};

watch(() => props.value, updateState, { immediate: true, deep: true });

const nullableValue = value =>
  props.allowInherit && value === 'inherit' ? null : value;

const buildPayload = () => ({
  enabled:
    state.enabledState === 'inherit' ? null : state.enabledState === 'enabled',
  autonomy_mode: nullableValue(state.autonomyMode),
  automatic_confidence_threshold: state.confidenceThresholdInherited
    ? null
    : state.confidenceThreshold,
  redaction_enabled: state.redactionInherited ? null : state.redactionEnabled,
  log_retention_days: state.logRetentionInherited
    ? null
    : state.logRetentionDays,
});

const handleSave = () => emit('save', buildPayload());
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
    <div class="mb-4">
      <h4 class="text-sm font-medium text-n-slate-12">{{ title }}</h4>
      <p class="mt-1 text-sm text-n-slate-11">{{ description }}</p>
    </div>

    <div class="grid gap-4 md:grid-cols-2">
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        <span>{{ t('CAPTAIN.AI_NATIVE.ENABLED_LABEL') }}</span>
        <select
          v-model="state.enabledState"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2"
        >
          <option
            v-for="option in enabledOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        <span>{{ t('CAPTAIN.AI_NATIVE.MODE_LABEL') }}</span>
        <select
          v-model="state.autonomyMode"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2"
        >
          <option
            v-for="option in modeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        <span class="flex items-center justify-between gap-2">
          {{ t('CAPTAIN.AI_NATIVE.THRESHOLD_LABEL') }}
          <span
            v-if="allowInherit"
            class="flex items-center gap-1 text-xs text-n-slate-11"
          >
            <input
              v-model="state.confidenceThresholdInherited"
              type="checkbox"
            />
            {{ t('CAPTAIN.AI_NATIVE.INHERIT') }}
          </span>
        </span>
        <input
          v-model.number="state.confidenceThreshold"
          min="0"
          max="1"
          step="0.05"
          type="number"
          :disabled="state.confidenceThresholdInherited"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2"
        />
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        <span class="flex items-center justify-between gap-2">
          {{ t('CAPTAIN.AI_NATIVE.RETENTION_LABEL') }}
          <span
            v-if="allowInherit"
            class="flex items-center gap-1 text-xs text-n-slate-11"
          >
            <input v-model="state.logRetentionInherited" type="checkbox" />
            {{ t('CAPTAIN.AI_NATIVE.INHERIT') }}
          </span>
        </span>
        <input
          v-model.number="state.logRetentionDays"
          min="1"
          max="3650"
          step="1"
          type="number"
          :disabled="state.logRetentionInherited"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2"
        />
      </label>
    </div>

    <div class="mt-4 flex items-center justify-between gap-4">
      <div>
        <h5 class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.AI_NATIVE.REDACTION_LABEL') }}
          <label
            v-if="allowInherit"
            class="flex items-center gap-1 text-xs font-normal text-n-slate-11"
          >
            <input v-model="state.redactionInherited" type="checkbox" />
            {{ t('CAPTAIN.AI_NATIVE.INHERIT') }}
          </label>
        </h5>
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.AI_NATIVE.REDACTION_DESCRIPTION') }}
        </p>
      </div>
      <Switch
        v-model="state.redactionEnabled"
        :disabled="state.redactionInherited"
      />
    </div>

    <div class="mt-4">
      <Button
        :label="t('CAPTAIN.AI_NATIVE.SAVE')"
        size="sm"
        @click="handleSave"
      />
    </div>
  </div>
</template>
